import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Session store shared by the merchant, employee and Quantum portals.
 *
 * The access token is short-lived (15 minutes) and kept in sessionStorage
 * rather than localStorage: closing the tab ends the session, which is the
 * behaviour a treasury console should have. The refresh token is held in an
 * httpOnly cookie set by the API and is never readable here.
 */

export interface Session {
  accessToken: string;
  user: {
    id: string;
    email: string;
    role: string;
    merchant_id?: string;
    totp_enabled?: boolean;
  };
  /** Merchant portal only — the dashboard's Test/Live toggle. */
  mode?: 'TEST' | 'LIVE';
}

export interface AuthState {
  session: Session | null;
  isAuthenticated: boolean;
  signIn: (session: Session) => void;
  signOut: () => void;
  setMode: (mode: 'TEST' | 'LIVE') => void;
  getToken: () => string | null;
}

/**
 * One store per app, namespaced by `storageKey`, so signing into the merchant
 * portal in one tab never leaks a token into the employee portal in another.
 */
export function createAuthStore(storageKey: string) {
  return create<AuthState>()(
    persist(
      (set, get) => ({
        session: null,
        isAuthenticated: false,

        signIn: (session) => set({ session, isAuthenticated: true }),

        signOut: () => {
          set({ session: null, isAuthenticated: false });
          try {
            sessionStorage.removeItem(storageKey);
          } catch {
            /* storage disabled — the in-memory clear above is enough */
          }
        },

        setMode: (mode) => {
          const current = get().session;
          if (current) set({ session: { ...current, mode } });
        },

        getToken: () => get().session?.accessToken ?? null,
      }),
      {
        name: storageKey,
        storage: createJSONStorage(() => sessionStorage),
        // Only the session is worth rehydrating; the actions are rebuilt on
        // every load and JSON would drop them anyway.
        partialize: (state) => ({ session: state.session, isAuthenticated: state.isAuthenticated }),
      },
    ),
  );
}
